import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { UserPlus, Trash2, Shield, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data.users);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/admin/users`, form, { withCredentials: true });
      setShowForm(false);
      setForm({ name: '', email: '', password: '', role: 'admin' });
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to create user');
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this admin user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`, { withCredentials: true });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <div className="p-8" data-testid="admin-users">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white" data-testid="users-title">Admin Users</h1>
        {isSuperAdmin && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            data-testid="add-user-btn"
          >
            <UserPlus size={16} /> Add Admin
          </button>
        )}
      </div>

      {/* Create User Form */}
      {showForm && (
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-6" data-testid="create-user-form">
          <h2 className="text-lg font-semibold text-white mb-4">New Admin User</h2>
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
          <form onSubmit={createUser} className="grid sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="bg-slate-700 border border-slate-600 rounded-lg text-white text-sm px-4 py-2.5 placeholder-slate-500 focus:ring-2 focus:ring-blue-500"
              data-testid="new-user-name"
            />
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="bg-slate-700 border border-slate-600 rounded-lg text-white text-sm px-4 py-2.5 placeholder-slate-500 focus:ring-2 focus:ring-blue-500"
              data-testid="new-user-email"
            />
            <input
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              className="bg-slate-700 border border-slate-600 rounded-lg text-white text-sm px-4 py-2.5 placeholder-slate-500 focus:ring-2 focus:ring-blue-500"
              data-testid="new-user-password"
            />
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="bg-slate-700 border border-slate-600 rounded-lg text-white text-sm px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
              data-testid="new-user-role"
            >
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
            <div className="sm:col-span-2 flex gap-3">
              <button type="submit" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="submit-user-btn">Create User</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden" data-testid="users-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700 text-slate-400">
              <th className="text-left py-3 px-5 font-medium">Name</th>
              <th className="text-left py-3 px-5 font-medium">Email</th>
              <th className="text-left py-3 px-5 font-medium">Role</th>
              <th className="text-left py-3 px-5 font-medium">Created</th>
              <th className="py-3 px-5"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500">Loading...</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-b border-slate-700/50 hover:bg-slate-700/20" data-testid={`user-row-${u.id}`}>
                <td className="py-3 px-5 text-white font-medium">{u.name}</td>
                <td className="py-3 px-5 text-slate-300">{u.email}</td>
                <td className="py-3 px-5">
                  <span className={`flex items-center gap-1.5 text-xs font-medium ${u.role === 'super_admin' ? 'text-amber-400' : 'text-blue-400'}`}>
                    {u.role === 'super_admin' ? <ShieldCheck size={14} /> : <Shield size={14} />}
                    {u.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </span>
                </td>
                <td className="py-3 px-5 text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                <td className="py-3 px-5">
                  {isSuperAdmin && u.id !== currentUser.id && (
                    <button onClick={() => deleteUser(u.id)} className="text-slate-500 hover:text-red-400 transition-colors" data-testid={`delete-user-${u.id}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
